import { UI_PRIMITIVE__radio_group } from '@/components/ui/radio_group/UI_PRIMITIVE__radio_group';
import { UI_PRIMITIVE__radio_group_item } from '@/components/ui/radio_group/UI_PRIMITIVE__radio_group_item';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_ui } from '@/hooks/store/use_slices';
import { LOCAL__ui_g_inputs__filter_set } from '@/state/ui/generation/LOCAL__ui_g_inputs__filter_set';
import { Label } from '@radix-ui/react-label';
import { DTO__generation_inputs_filter } from '@shared/dto/generation/DTO__generation_inputs_filter';

/**
 * Keys of PAYLOAD__generation_inputs_filter that store string/union values (e.g. 'yes' | 'no' | 'all')
 */
type TYPE__radio_filter_keys = {
  [K in keyof DTO__generation_inputs_filter]: DTO__generation_inputs_filter[K] extends string ? K : never;
}[keyof DTO__generation_inputs_filter];

export interface PROPS<K extends TYPE__radio_filter_keys> {
  label: string;
  filter_key: K;
  values_all?: readonly DTO__generation_inputs_filter[K][];
  option_labels?: Record<string, string>;
  children?: React.ReactNode;
}

const DEFAULT_OPTIONS = ['all', 'yes', 'no'] as const;

/*
 *
 */

export function G_FILTER__RADIO_GROUP__inputs_field_type<K extends TYPE__radio_filter_keys>({
  label,
  filter_key,
  children = null,
  values_all = DEFAULT_OPTIONS as unknown as DTO__generation_inputs_filter[K][],
}: PROPS<K>) {
  const dispatch = use_dispatch();
  /*
   *
   */

  const { g_inputs__filter } = use_slice_ui(['g_inputs__filter']);

  /*
   *
   */

  // Current selected string value stored in state
  const value_current = g_inputs__filter[filter_key] as string;

  function onValueChange(val: string) {
    const filter_new = g_inputs__filter.clone({ [filter_key]: val });

    dispatch(LOCAL__ui_g_inputs__filter_set(filter_new));
  }

  /*
   *
   */

  return (
    <div className="flex flex-col w-full">
      <div className="flex items-center justify-between text-xs text-bar-foreground px-2 py-1">
        <span>{label}</span>
        {/* 4. Render children at the end of the title row */}
        {children && <div className="flex items-center gap-1">{children}</div>}
      </div>
      <UI_PRIMITIVE__radio_group value={value_current} {...{ onValueChange }} className="flex items-center gap-2 px-2 bg-component py-1 rounded-sm">
        {values_all.map(option => {
          const id = `${filter_key}_radio_${option}`;
          const is_selected = value_current === option;

          return (
            <div
              key={id}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-sm cursor-pointer border transition-colors ${
                is_selected ? 'border-component-item-selected bg-component-item-selected/10' : 'border-transparent hover:bg-component-item-hover/50'
              }`}
            >
              <UI_PRIMITIVE__radio_group_item
                value={String(option)}
                id={id}
                className={is_selected ? '!border-component-item-foreground' : '!border-bar-foreground'}
                class_name_indicator={is_selected ? 'text-component-item-foreground' : 'text-bar-foreground'}
                class_name_icon={is_selected ? 'text-component-item-foreground' : 'text-bar-foreground'}
              />
              <Label
                htmlFor={id}
                className={`
                text-xs 
                ${is_selected ? 'text-component-item-foreground' : 'text-bar-foreground'} 
                cursor-pointer capitalize font-normal select-none`}
              >
                {option}
              </Label>
            </div>
          );
        })}
      </UI_PRIMITIVE__radio_group>
    </div>
  );
}
